import { HttpResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ModalData, ModalRef } from '@purespectrum1/ui/modal';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import * as fileSaver from 'file-saver';
import { EMPTY, Subscription, timer } from 'rxjs';
import { catchError, switchMap, takeWhile, tap } from 'rxjs/operators';
import {
  SupHashRemovalJob,
  SupHashRemovalModalData,
} from '../../operator-settings.interface';
import { OperatorSettingsService } from '../../operator-settings.service';

@Component({
  selector: 'ps-sup-hash-removal-history-modal',
  templateUrl: './sup-hash-removal-history-modal.component.html',
  styleUrls: ['./sup-hash-removal-history-modal.component.css'],
})
export class SupHashRemovalHistoryModalComponent implements OnInit, OnDestroy {
  jobs: SupHashRemovalJob[] = [];
  isLoading = false;
  downloadingJobId?: string;
  private _subscriptions = new Subscription();

  constructor(
    private _modalRef: ModalRef<SupHashRemovalHistoryModalComponent, string>,
    @Inject(ModalData) public data: SupHashRemovalModalData,
    private _operatorSettingsService: OperatorSettingsService,
    private _toastr: ToasterService
  ) {}

  ngOnInit(): void {
    this.getHistory();
    if (this.data?.jobId) {
      this._pollJob(this.data.jobId);
    }
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  getHistory() {
    this.isLoading = true;
    const history = this._operatorSettingsService
      .getSupHashRemovalHistory()
      .subscribe({
        next: (response) => {
          this.jobs = response.data || [];
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this._showError(error, 'Unable to load supHashId removal history.');
        },
      });
    this._subscriptions.add(history);
  }

  progress(job: SupHashRemovalJob): number {
    if (job.status === 'Completed') {
      return 100;
    }
    if (!job.validRows) {
      return 0;
    }
    return Math.min(100, Math.round((job.processedRows / job.validRows) * 100));
  }

  displayStatus(job: SupHashRemovalJob): string {
    if (
      job.status === 'Completed' &&
      (job.invalidRows > 0 ||
        (job.mismatchRows || 0) > 0 ||
        (job.verificationFailedRows || 0) > 0 ||
        (job.partiallyRemovedRows || 0) > 0 ||
        job.failedRows > 0)
    ) {
      return 'Completed with issues';
    }
    return job.status;
  }

  downloadReport(job: SupHashRemovalJob) {
    if (!job.reportAvailable || this.downloadingJobId) {
      return;
    }

    this.downloadingJobId = job.jobId;
    const download = this._operatorSettingsService
      .downloadSupHashRemovalReport(job.jobId)
      .subscribe({
        next: (response) => {
          const filename = this._getReportFilename(response, job);
          fileSaver.saveAs(response.body as Blob, filename);
          this.downloadingJobId = undefined;
        },
        error: (error) => {
          this.downloadingJobId = undefined;
          this._showError(error, 'Unable to download the result report.');
        },
      });
    this._subscriptions.add(download);
  }

  close() {
    this._modalRef.close();
  }

  private _pollJob(jobId: string) {
    const polling = timer(0, 3000)
      .pipe(
        switchMap(() =>
          this._operatorSettingsService.getSupHashRemovalJob(jobId)
        ),
        tap((job) => {
          this._upsertJob(job);
          if (this._isTerminal(job)) {
            this.getHistory();
          }
        }),
        takeWhile((job) => !this._isTerminal(job), true),
        catchError((error) => {
          this._showError(error, 'Unable to refresh the removal status.');
          return EMPTY;
        })
      )
      .subscribe();
    this._subscriptions.add(polling);
  }

  private _upsertJob(job: SupHashRemovalJob) {
    const index = this.jobs.findIndex((item) => item.jobId === job.jobId);
    if (index === -1) {
      this.jobs = [job, ...this.jobs];
      return;
    }
    this.jobs = this.jobs.map((item) =>
      item.jobId === job.jobId ? job : item
    );
  }

  private _isTerminal(job: SupHashRemovalJob) {
    return job.status === 'Completed' || job.status === 'Failed';
  }

  private _getReportFilename(
    response: HttpResponse<Blob>,
    job: SupHashRemovalJob
  ) {
    const disposition = response.headers.get('Content-Disposition');
    const match = disposition?.match(/filename="?([^";]+)"?/i);
    return match?.[1] || `suphash-removal-${job.jobId}.csv`;
  }

  private _showError(error: any, fallback: string) {
    this._toastr.error(error?.error?.msg || fallback);
  }
}
